import { useState, useEffect, useCallback } from "react";
import axios from "axios";

// New Data frontend data layer (docs/michael/TASK-new-data-tab.md §7 S16).
// All calls are axios POSTs against /new-data/* — axios only, because the
// bearer-token interceptor and the global loading sweep are axios-only.

const API = process.env.REACT_APP_API_URL;

// Masters only — same hardcoded pair as SupplierRetailerContext/AuthenticatedLayout.
export const isMasterUser = () => {
  const userid = localStorage.getItem("userid");
  return userid === "1" || userid === "2";
};

// An organisation admin with Imports (Blue Mango, 2026-10-02) gets a SCOPED New
// Data — its own suppliers' articles + its own org's held rows, never stores —
// served by /org-new-data. Masters keep /new-data. Same request/response shapes.
export const isOrgNewDataUser = () => {
  try { return !isMasterUser() && localStorage.getItem("user_type") === "org_admin"; } catch (e) { return false; }
};
const base = () => `${API}/${isOrgNewDataUser() ? "org-new-data" : "new-data"}`;

// Legacy pattern: every authenticated call also carries userid in the body.
const withUserid = (extra = {}) => ({ userid: localStorage.getItem("userid"), ...extra });

// Controllers in this codebase answer either flat ({tableReady, ...}) or
// wrapped ({data: ...}); accept both so the callers never care.
const unwrap = (res) => {
  const d = res?.data;
  if (d && typeof d === "object" && d.data !== undefined) return d.data;
  return d;
};

export const fetchSummary = async () => {
  const res = await axios.post(`${base()}/summary`, withUserid());
  return unwrap(res);
};

// {status, kind?, retailer?, month?} — status 'pending' for the steppers,
// 'resolved' (= everything non-pending) for the month history.
export const fetchList = async ({ status, kind, retailer, month } = {}) => {
  const res = await axios.post(`${base()}/list`, withUserid({ status, kind, retailer, month }));
  const d = unwrap(res);
  if (Array.isArray(d)) return d;
  return d?.items || d?.rows || [];
};

// {id, site_code, site_description, region}
export const addStore = async (body) => unwrap(await axios.post(`${base()}/add-store`, withUserid(body)));

// {id, description, unitcost, brand, suppliercode, activeyn}
export const addArticle = async (body) => unwrap(await axios.post(`${base()}/add-article`, withUserid(body)));

// action: 'ignore' | 'exclude' | 'dc' (exclude/dc are store-only — server enforces)
export const resolveItems = async (ids, action) => unwrap(await axios.post(`${base()}/resolve`, withUserid({ ids, action })));

export const reopenItem = async (id) => unwrap(await axios.post(`${base()}/reopen`, withUserid({ id })));

// ── PnP v2 unmatched-row holds (TASK-vendorcode-v2.md S03b) ──────────────────
// Grouped rows: {vendor_code, article_code, article_description, data_type,
// source, filename, file_suppliers, row_count, first_seen, uploaded_by, ids}
export const fetchHolds = async (status = "held") => {
  const res = await axios.post(`${base()}/holds-list`, withUserid({ status }));
  const d = unwrap(res);
  return Array.isArray(d) ? d : [];
};

// Assign a supplier to a group: maps the vendor code (when there is one) and
// replays the held rows through the v2 import immediately.
export const resolveHolds = async ({ ids, vendor_code, suppliercode }) =>
  unwrap(await axios.post(`${base()}/holds-resolve`, withUserid({ ids, vendor_code, suppliercode })));

export const discardHolds = async (ids) =>
  unwrap(await axios.post(`${base()}/holds-discard`, withUserid({ ids })));

/**
 * Loads the summary AND the pending items for both steppers in one go —
 * the summary alone is counts; nothing else loads the items (S16). Used by
 * the login popup and the Imports → New Data tab.
 */
export function useNewDataQueue({ auto = true } = {}) {
  const [summary, setSummary] = useState(null);
  const [storeItems, setStoreItems] = useState([]);
  const [articleItems, setArticleItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await fetchSummary();
      setSummary(s || null);
      if (s && s.tableReady) {
        const [stores, articles] = await Promise.all([
          fetchList({ status: "pending", kind: "store" }),
          fetchList({ status: "pending", kind: "article" }),
        ]);
        setStoreItems(stores);
        setArticleItems(articles);
      } else {
        setStoreItems([]);
        setArticleItems([]);
      }
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (auto) refresh();
  }, [auto, refresh]);

  return { summary, storeItems, articleItems, loading, error, refresh };
}
